import { useCallback } from 'react';
import type { ReviewReason } from '../../../lib/employerReview/reviewReason.types';
import { markEmployerReviewPathTourDismissed } from '../../../lib/employerReview/employerReviewTourStorage';
import { useEmployerEvidenceTourOptional } from './EmployerEvidenceTourContext';
import { SpotlightTourOverlay } from './SpotlightTourOverlay';

function applyReviewPathStepEnter(
  stepIndex: number,
  firstFindingId: string | undefined,
  nav: NonNullable<ReturnType<typeof useEmployerEvidenceTourOptional>>,
) {
  if (stepIndex <= 2) {
    nav.setVerificationOpen(false);
    nav.setGateAnalysisOpen(false);
    nav.setFocusedFindingId(null);
    nav.setExpandedConcernId(null);
    return;
  }

  if (stepIndex === 3 && firstFindingId) {
    nav.setExpandedConcernId(firstFindingId);
    nav.setVerificationOpen(false);
    nav.setGateAnalysisOpen(false);
    nav.setFocusedFindingId(null);
    return;
  }

  if (stepIndex === 4 && firstFindingId) {
    nav.setExpandedConcernId(firstFindingId);
    nav.setVerificationOpen(true);
    nav.setGateAnalysisOpen(false);
    nav.setFocusedFindingId(firstFindingId);
    return;
  }

  if (stepIndex === 5) {
    if (firstFindingId) nav.setExpandedConcernId(firstFindingId);
    nav.setVerificationOpen(true);
    nav.setGateAnalysisOpen(true);
    if (firstFindingId) nav.setFocusedFindingId(firstFindingId);
  }
}

export function WhatsHappeningReviewPathTour({
  findings,
}: {
  findings: ReviewReason[];
  issueCount: number;
}) {
  const nav = useEmployerEvidenceTourOptional();
  const active = nav?.reviewPathTour ?? null;
  const firstFindingId = findings[0]?.id;

  const onStepEnter = useCallback(
    (stepIndex: number) => {
      if (!nav) return;
      applyReviewPathStepEnter(stepIndex, firstFindingId, nav);
    },
    [firstFindingId, nav],
  );

  if (!nav || !active) return null;

  const dismiss = () => {
    markEmployerReviewPathTourDismissed();
    nav.closeReviewPathTour();
  };

  return (
    <SpotlightTourOverlay
      steps={active.steps}
      stepIndex={active.stepIndex}
      ariaLabelId="employer-review-path-tour-title"
      onStepEnter={onStepEnter}
      onSkip={dismiss}
      onBack={() => nav.prevReviewPathStep()}
      onNext={() => {
        if (active.stepIndex >= active.steps.length - 1) dismiss();
        else nav.nextReviewPathStep();
      }}
    />
  );
}
